import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {getCurrentPageDuration} from '@Components/poster-editor/poster-editor-reducer';
import useWindowSize from '@Hooks/useWindowSize';
import {POSTER_MOBILE_TIMELINE_HEIGHT_RATIO} from '@Components/poster-editor/components/poster-editor-mobile-timeline/poster-editor-mobile-timeline.types';
import {getStepSizeFromWidthPerSecondForMobile} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timestamps/timestamps.helper';
import BaseTimeline from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline/base-timeline';
import {
  POSTER_EDITOR_MOBILE_BOTTOM_BAR_CONTAINER_ID,
  TIMELINE_MAX_WIDTH_PER_SECOND,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.types';
import {updatePanningStatus, updateTimelineWidthPerSecond} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {BaseTimestamps} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timestamps/base-timestamps';
import {onPosterPause, onPosterPlay} from '@Components/poster-editor/library/poster-editor-library';
import {noop} from '@Utils/general.util';
import type {Point} from '@Utils/math.util';
import {getDistanceBetweenPoints} from '@Utils/math.util';
import styles from './poster-editor-mobile-timeline.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

export function PosterEditorMobileTimeline(): ReactElement | null {
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const currentPageTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const isPosterPlaying = useAppSelector((state) => {
    return state.posterEditor.isPlaying;
  });

  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const initialPageDuration = useRef(currentPageDuration);
  const {windowHeight, windowWidth} = useWindowSize();
  const isDesignItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignResizing;
  });
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const isPanning = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isPanning;
  });
  const widthPerSecondRef = useRef(widthPerSecond);
  const durationWhileResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.durationWhileResizing;
  });
  const maxDurationWhileResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.maxDurationWhileResizing;
  });
  const wasPosterPlayingAtPointerDown = useRef(false);
  const seekValueAtPointerDown = useRef<number>();
  const dispatch = useAppDispatch();
  const [timelineWrapperRef, setTimelineWrapperRef] = useState<HTMLDivElement | null>();
  const [baseTimelineRef, setBaseTimelineRef] = useState<HTMLDivElement | null>();
  const pinchStartDistance = useRef<number | null>(null);
  const startingScrollValue = useRef<number | null>(null);
  const {isItemDragging, isItemResizing} = useAppSelector((state) => {
    return state.posterEditorWebBottomBar;
  });
  const isItemDraggingRef = useRef(isItemDragging);
  const isItemResizingRef = useRef(isItemResizing);

  useEffect((): void => {
    isItemDraggingRef.current = isItemDragging;
    isItemResizingRef.current = isItemResizing;
  }, [isItemDragging, isItemResizing]);

  useEffect((): void => {
    if (!isPanning) {
      widthPerSecondRef.current = widthPerSecond;
    }
  }, [isPanning]);

  const scrollToCurrentPageTime = (): void => {
    if (!timelineContainerRef?.current || !currentPageDuration) {
      return;
    }

    const percentage = currentPageTime / currentPageDuration;
    const totalScroll = timelineContainerRef.current.scrollWidth - timelineContainerRef.current.clientWidth;

    const newScrollValue = totalScroll * percentage;
    const isCloseToEnd = newScrollValue > totalScroll - 20 || newScrollValue === 0;
    timelineContainerRef.current.scroll({
      left: newScrollValue,
      behavior: isCloseToEnd ? 'smooth' : 'instant',
    });
  };

  const singleFingerPanHandler = (event: HammerInput): void => {
    if (isItemDraggingRef.current || isItemResizingRef.current) {
      return;
    }
    if (!timelineContainerRef.current) {
      return;
    }

    if (startingScrollValue.current === null) {
      startingScrollValue.current = timelineContainerRef.current.scrollLeft;
    }

    if (startingScrollValue.current === null) {
      return;
    }

    const calculatedNewScroll = startingScrollValue.current - event.deltaX;
    timelineContainerRef.current.scrollTo({
      left: calculatedNewScroll,
      behavior: 'instant',
    });
  };

  const targetTouches = (touches: Touch[]): Array<Point> => {
    return Array.from(touches).map((touch) => {
      return {
        x: touch.pageX,
        y: touch.pageY,
      };
    });
  };

  const updatePanningState = (status: boolean): void => {
    dispatch(updatePanningStatus(status));
  };

  const updateWidthPerSecond = (newWidth: number): void => {
    dispatch(updateTimelineWidthPerSecond(newWidth));
  };

  const pinchHandler = (event: HammerInput): void => {
    const touches = targetTouches(event.pointers as Touch[]);

    if (touches.length !== 2) {
      return;
    }

    if (!pinchStartDistance.current) {
      return;
    }

    const scale = widthPerSecondRef.current / TIMELINE_MAX_WIDTH_PER_SECOND;

    const distance = getDistanceBetweenPoints(touches[0], touches[1]);
    const difference = (distance - pinchStartDistance.current) * scale;

    updateWidthPerSecond(widthPerSecondRef.current + difference);
  };

  useEffect(() => {
    const element = timelineContainerRef.current;
    if (!element) {
      return noop;
    }
    const hammer = new Hammer(element);

    const singleFingerPan = new Hammer.Pan({
      event: 'singleFingerPan',
      direction: Hammer.DIRECTION_ALL,
      pointers: 1,
    });

    const pinch = new Hammer.Pinch({
      event: 'pinch',
    });

    hammer.add([pinch, singleFingerPan]);
    hammer.on('panright panleft', singleFingerPanHandler);
    hammer.on('panend', (): void => {
      startingScrollValue.current = null;
    });
    hammer.on('pinchmove', pinchHandler);
    hammer.on('pinchstart', (event): void => {
      const touches = targetTouches(event.pointers as Touch[]);

      if (touches.length !== 2) {
        return;
      }

      pinchStartDistance.current = getDistanceBetweenPoints(touches[0], touches[1]);
      updatePanningState(true);
    });
    hammer.on('pinchend', (): void => {
      pinchStartDistance.current = null;
      updatePanningState(false);
    });

    return (): void => {
      widthPerSecondRef.current = widthPerSecond;
      hammer.destroy();
    };
  }, [timelineContainerRef]);

  useEffect(() => {
    if (currentPageTime === 0) {
      void window.posterEditor?.whiteboard?.seek(0.01);
    }
    scrollToCurrentPageTime();
    dispatch(updateTimelineWidthPerSecond(80));
  }, []);

  useEffect((): void => {
    if (!isPosterPlaying) {
      return;
    }

    scrollToCurrentPageTime();
  }, [currentPageTime]);

  const onScroll = (): void => {
    if (isPosterPlaying) {
      return;
    }
    if (!timelineContainerRef.current || !currentPageDuration) {
      return;
    }
    const totalScroll = timelineContainerRef.current.scrollWidth - timelineContainerRef.current.clientWidth;
    const {scrollLeft} = timelineContainerRef.current;

    const newPageDuration = (scrollLeft * currentPageDuration) / totalScroll;
    void window.posterEditor?.whiteboard?.seek(newPageDuration);
  };

  if (!currentPageDuration || !initialPageDuration.current) {
    return null;
  }

  const getTimelineHeightForMobile = (): number => {
    return windowHeight * POSTER_MOBILE_TIMELINE_HEIGHT_RATIO;
  };

  const getWidthStyle = (offset = 0): string => {
    if (isDesignItemResizing) {
      return `${Math.max(durationWhileResizing, maxDurationWhileResizing, currentPageDuration) * widthPerSecond + offset}px`;
    }
    return `${widthPerSecond * currentPageDuration + offset}px`;
  };

  const getMarginTop = (): string => {
    if (!timelineWrapperRef || !baseTimelineRef) {
      return '0px';
    }

    const halfHeightOfWrapper = timelineWrapperRef.clientHeight / 2;
    const heightOfTimeline = baseTimelineRef.clientHeight;
    const margin = halfHeightOfWrapper - heightOfTimeline;

    if (margin < 0) {
      return '0px';
    }

    return `${margin}px`;
  };

  const getPaddingRight = (): string => {
    const differenceInActualAndAssignedWidth = (Math.ceil(currentPageDuration) - currentPageDuration) * widthPerSecond;
    const paddingRight = windowWidth / 2 - differenceInActualAndAssignedWidth;
    return `${paddingRight}px`;
  };

  const onPointerDown = (): void => {
    if (isPosterPlaying) {
      onPosterPause();
      wasPosterPlayingAtPointerDown.current = true;
    }
    seekValueAtPointerDown.current = currentPageTime;
  };

  const onPointerUp = (): void => {
    if (!wasPosterPlayingAtPointerDown.current) {
      return;
    }

    if (!seekValueAtPointerDown.current) {
      return;
    }

    if (Math.abs(currentPageTime - seekValueAtPointerDown.current) <= 0.05) {
      onPosterPlay();
    }

    wasPosterPlayingAtPointerDown.current = false;
    seekValueAtPointerDown.current = undefined;
  };

  return (
    <div id={POSTER_EDITOR_MOBILE_BOTTOM_BAR_CONTAINER_ID} className={styles.wrapper}>
      <div className={`${styles.seekHandleContainer} _full-height flex-v-row align-items-center`}>
        <div className={styles.seekHandleUpper} />
        <div className={styles.seekHandleLower} />
      </div>
      <div
        id="timeline-container"
        style={{
          height: `${getTimelineHeightForMobile()}px`,
          paddingRight: getPaddingRight(),
        }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onScroll={onScroll}
        ref={timelineContainerRef}
        className={`${styles.container} flex-v-row  _full-width spacing-p-t-2`}
      >
        <BaseTimestamps
          className={` _fit-width ${styles.timestamps}`}
          timelineContainerRef={timelineContainerRef}
          widthPerSecond={widthPerSecond}
          duration={isDesignItemResizing ? Math.max(durationWhileResizing, maxDurationWhileResizing, currentPageDuration) : currentPageDuration}
          stepSize={getStepSizeFromWidthPerSecondForMobile(currentPageDuration)}
        />

        <div
          ref={setTimelineWrapperRef}
          className="_full-height"
          style={{
            width: getWidthStyle(),
          }}
        >
          <div
            style={{
              marginTop: getMarginTop(),
            }}
            className={styles.baseTimelineWrapper}
            ref={setBaseTimelineRef}
          >
            <BaseTimeline />
          </div>
        </div>
      </div>
    </div>
  );
}
